"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ApiError, translateApiError } from "@/lib/api/error";
import type { components } from "@/lib/api.gen";
import { loginStartEmail2fa, loginVerifyEmail2fa, loginVerifyRecovery, loginVerifyTotp, loginVerifyWebauthn } from "@/lib/api/two-factor";
type SecondFactorMethod = components["schemas"]["SecondFactorMethod"];

// Order methods so the strongest / most convenient is offered first.
const METHOD_ORDER: SecondFactorMethod[] = ["webauthn", "totp", "email", "recovery_code"];
interface Props {
  methods: SecondFactorMethod[];
  onSuccess: () => void | Promise<void>;
  /** Hide the internal heading (e.g. when a DialogTitle already renders it). */
  hideHeader?: boolean;
}
export function TwoFactorChallenge({
  methods,
  onSuccess,
  hideHeader
}: Props) {
  const t = useTranslations();
  const ordered = METHOD_ORDER.filter(m => methods.includes(m));
  const [method, setMethod] = useState<SecondFactorMethod>(ordered[0] ?? methods[0]);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<"idle" | "waiting" | null>(null);
  const [passphrase, setPassphrase] = useState<number[] | null>(null);
  const eventsRef = useRef<EventSource | null>(null);
  const words = t.raw("auth.email-login.words") as string[];
  useEffect(() => {
    return () => eventsRef.current?.close();
  }, []);
  const handleError = (e: unknown) => {
    setError(translateApiError(ApiError.from(e), t));
  };
  const finalize = async () => {
    setError(null);
    await onSuccess();
  };
  const submitTotp = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await loginVerifyTotp(code.trim());
      await finalize();
    } catch (e_0) {
      handleError(e_0);
    } finally {
      setIsLoading(false);
    }
  };
  const submitRecovery = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await loginVerifyRecovery(recovery.trim());
      await finalize();
    } catch (e_1) {
      handleError(e_1);
    } finally {
      setIsLoading(false);
    }
  };
  const submitWebauthn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await loginVerifyWebauthn();
      await finalize();
    } catch (e_2) {
      // A cancelled ceremony throws a DOMException, not an ApiError.
      if (e_2 instanceof ApiError) {
        handleError(e_2);
      } else {
        setError(t("auth.two-factor.webauthn.error"));
      }
    } finally {
      setIsLoading(false);
    }
  };
  const startEmail = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const indices = await loginStartEmail2fa();
      setPassphrase(indices);
      setEmailStatus("waiting");
      const subscribeUrl = new URL("/api/sse-events", window.location.origin);
      eventsRef.current?.close();
      const events = new EventSource(subscribeUrl.toString(), {
        withCredentials: true
      });
      eventsRef.current = events;
      events.addEventListener("login-verified", async () => {
        events.close();
        try {
          await loginVerifyEmail2fa();
          await finalize();
        } catch (e_4) {
          handleError(e_4);
        }
      });
      events.onerror = () => {
        events.close();
        setError(t("auth.email-login.error.connection-error"));
        setEmailStatus(null);
      };
    } catch (e_3) {
      handleError(e_3);
      setEmailStatus(null);
    } finally {
      setIsLoading(false);
    }
  };
  const methodLabel = (m_0: SecondFactorMethod) => t(`auth.two-factor.method.${m_0}`);
  return <div className="grid gap-4" data-testid="two-factor-challenge">
      <div>
        {!hideHeader && <h3 className="text-lg font-semibold">{t("auth.two-factor.title")}</h3>}
        <p className="text-sm text-muted-foreground">
          {t(`auth.two-factor.description.${method}`)}
        </p>
      </div>

      {method === "totp" && <div className="grid gap-2">
          <Label>{t("auth.two-factor.totp.label")}</Label>
          <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={submitTotp} disabled={isLoading} data-testid="totp-input">
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map(i => <InputOTPSlot key={i} index={i} />)}
            </InputOTPGroup>
          </InputOTP>
          <Button onClick={submitTotp} disabled={isLoading || code.trim().length < 6}>
            {isLoading ? t("auth.two-factor.verifying") : t("auth.two-factor.totp.action")}
          </Button>
        </div>}

      {method === "recovery_code" && <div className="grid gap-2">
          <Label htmlFor="recovery-code">{t("auth.two-factor.recovery.label")}</Label>
          <Input id="recovery-code" value={recovery} onChange={e_5 => setRecovery(e_5.target.value)} placeholder={t("auth.two-factor.recovery.placeholder")} autoComplete="one-time-code" disabled={isLoading} data-testid="recovery-input" />
          <Button onClick={submitRecovery} disabled={isLoading || !recovery.trim()}>
            {isLoading ? t("auth.two-factor.verifying") : t("auth.two-factor.recovery.action")}
          </Button>
        </div>}

      {method === "webauthn" && <div className="grid gap-2">
          <p className="text-sm text-muted-foreground">{t("auth.two-factor.webauthn.hint")}</p>
          <Button onClick={submitWebauthn} disabled={isLoading} data-testid="webauthn-button">
            {isLoading ? t("auth.two-factor.verifying") : t("auth.two-factor.webauthn.action")}
          </Button>
        </div>}

      {method === "email" && <div className="grid gap-2">
          {emailStatus === "waiting" ? <div className="rounded-md border bg-muted/50 p-3 text-sm">
              <p>{t("auth.email-login.check-inbox")}</p>
              {passphrase && <p className="mt-1 font-mono text-base font-semibold tracking-wide" data-testid="login-passphrase">
                  {passphrase.map(i_0 => words[i_0]).join("-")}
                </p>}
            </div> : <Button onClick={startEmail} disabled={isLoading}>
              {isLoading ? t("auth.two-factor.verifying") : t("auth.two-factor.email.action")}
            </Button>}
        </div>}

      {error && <p className="text-destructive text-sm">{error}</p>}

      {ordered.length > 1 && <div className="flex flex-wrap gap-2 border-t pt-3 text-sm">
          <span className="text-muted-foreground">{t("auth.two-factor.choose-other")}</span>
          {ordered.filter(m_1 => m_1 !== method).map(m_2 => <Button key={m_2} type="button" variant="link" className="h-auto p-0 align-baseline text-sm font-normal underline" onClick={() => {
        setMethod(m_2);
        setError(null);
        setEmailStatus(null);
        eventsRef.current?.close();
      }}>
                {methodLabel(m_2)}
              </Button>)}
        </div>}
    </div>;
}